/**
 * Criar caixas de relés e fusíveis em lote.
 *
 * **O que o lote multiplica são veículos × anos.** Uma caixa por par — o maior
 * projeto tem 504 caixas, uma por modelo, e é assim que elas nascem. As
 * *posições* (identificadores) não multiplicam: vão todas juntas em cada caixa
 * criada, e são elas que dizem quais linhas do projeto cada caixa mostra.
 *
 * **A conta aparece antes.** Escolher 12 veículos e 8 anos cria 96 caixas de uma
 * vez, e ninguém deveria descobrir isso depois — criar é fácil, apagar 96 caixas
 * uma a uma não é. A multiplicação é do lado de cá e dá para mostrar exata.
 *
 * **Um campo embaixo do outro.** A coluna tem 330px; os campos lado a lado com
 * largura fixa quebravam em pedaços tortos, e o `<select multiple>` de 92px de
 * altura escondia o que estava marcado. Agora os veículos são uma lista de marcar
 * com filtro, e o que foi escolhido (veículos, anos, posições) aparece em etiquetas.
 */

import { useMemo, useState } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { api, foraDoProjeto } from "@/api/cliente"
import { casaBusca } from "@/kit/buscaDeTexto"
import { SeletorComBusca } from "@/kit/SeletorComBusca"
import type { CaixaDeReles } from "@/api/outrosProjetos"
import { Botao, Campo, Etiqueta, estiloCampo, mensagem, type Nomes } from "./pecas"

/**
 * A caixa de onde o "incluir modelos" parte. Título, versão, motor, sistema e posições
 * vêm dela e não se editam: um título com uma letra diferente viraria outra central, e
 * uma posição a menos faria a caixa nova não mostrar as linhas.
 */
export type CaixaDeOrigem = {
  caixa: CaixaDeReles
  posicoes: string[]
  /** Quantas fotos ela tem: com zero, a pergunta de copiar nem aparece. */
  fotos: number
}

export function CaixasEmLote({ projetoId, nomes, invalidar, aoAvisar, aPartirDe, aoFechar }: {
  projetoId: number
  nomes: Nomes
  invalidar: (string | number)[][]
  aoAvisar: (m: string | null) => void
  /** "Incluir modelos": o lote já abre, com o que é da caixa preenchido e travado. */
  aPartirDe?: CaixaDeOrigem
  /** Com `aPartirDe`, quem abriu decide quando fechar. */
  aoFechar?: () => void
}) {
  const qc = useQueryClient()
  const [abertoAqui, setAbertoAqui] = useState(false)
  const aberto = aPartirDe ? true : abertoAqui
  const setAberto = (v: boolean) => (aPartirDe ? !v && aoFechar?.() : setAbertoAqui(v))

  const origem = aPartirDe?.caixa
  const [titulo, setTitulo] = useState(origem?.titulo ?? "")
  const [versao, setVersao] = useState(origem?.versao ?? "")
  const [veiculos, setVeiculos] = useState<number[]>([])
  const [motorId, setMotorId] = useState(origem?.motorId ?? 0)
  const [sistemaId, setSistemaId] = useState(origem?.sistemaId ?? 0)
  const [anosTexto, setAnosTexto] = useState(origem?.ano ? String(origem.ano) : "")
  const [identificadores, setIdentificadores] = useState(aPartirDe?.posicoes.join(", ") ?? "")
  // Copiar as fotos é escolha de quem inclui: começa desmarcado.
  const [copiarFotos, setCopiarFotos] = useState(false)

  /**
   * Os anos vêm de texto solto — "2018 2019 2020" ou "2018,2019" ou uma faixa
   * "2018-2021", que é como as pessoas escrevem. Mostrar o que foi entendido
   * evita criar caixa de ano nenhum por causa de um separador.
   */
  const anos = useMemo(() => lerAnos(anosTexto), [anosTexto])
  const posicoes = useMemo(() => lerPosicoes(identificadores), [identificadores])

  const quantas = veiculos.length * anos.length

  const motores = useMemo(() => ordenadas(nomes.motor), [nomes.motor])
  const sistemas = useMemo(() => ordenadas(nomes.sistema), [nomes.sistema])

  const criar = useMutation({
    mutationFn: async () => {
      const r = await api.post<{
        criadas: number; ignoradas: number; pedidas: number
        imagensCopiadas?: number; imagensNaoCopiadas?: number
      }>(
        `api/reles-fusiveis/${projetoId}/caixas-lote`, {
          projetoId, titulo, versao,
          veiculosIds: veiculos, motorId, sistemaId,
          anos,
          identificadores: posicoes,
          copiarImagensDaCaixaId: origem && copiarFotos ? origem.id : null,
        }, foraDoProjeto())
      return r.data
    },
    onSuccess: (r) => {
      for (const k of invalidar) qc.invalidateQueries({ queryKey: k })
      // Quem diz quantas foram é o servidor, não a nossa conta de veículos x anos:
      // caixa que já existe é ignorada, e antes disso ela era duplicada em silêncio.
      const partes = [
        r.ignoradas > 0
          ? `${r.criadas} caixa(s) criadas; ${r.ignoradas} já existiam e foram ignoradas.`
          : `${r.criadas} caixa(s) criadas.`,
      ]
      if (r.imagensCopiadas) partes.push(`${r.imagensCopiadas} foto(s) copiadas.`)
      if (r.imagensNaoCopiadas) partes.push(`${r.imagensNaoCopiadas} foto(s) não puderam ser copiadas.`)
      aoAvisar(partes.join(" "))
      setAberto(false)
    },
    onError: (e) => aoAvisar(mensagem(e)),
  })

  if (!aberto) {
    return (
      <Botao aoClicar={() => { setAberto(true); aoAvisar(null) }}>
        Criar caixas em lote…
      </Botao>
    )
  }

  return (
    <div style={{
      marginTop: 10, padding: 12, borderRadius: 8, border: "1px solid var(--line)",
      background: "var(--surface2)",
    }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 10 }}>
        <b style={{ fontSize: 12.5 }}>{origem ? "Incluir modelos" : "Caixas em lote"}</b>
        <span style={{ fontSize: 10.5, color: "var(--txt3)" }}>uma caixa por veículo × ano</span>
        <button onClick={() => setAberto(false)} aria-label="Fechar" style={{
          marginLeft: "auto", border: 0, background: "transparent", cursor: "pointer",
          color: "var(--txt3)", fontSize: 14, lineHeight: 1,
        }}>×</button>
      </div>

      {origem && aPartirDe ? (
        <Bloco titulo="Igual a esta caixa">
          <dl style={{ margin: "0 0 8px", display: "grid", gridTemplateColumns: "auto 1fr", gap: "3px 10px", fontSize: 11.5 }}>
            <dt style={rotuloDaOrigem}>Título</dt><dd style={{ margin: 0 }}><b>{origem.titulo}</b></dd>
            {origem.versao?.trim() && <><dt style={rotuloDaOrigem}>Versão</dt><dd style={{ margin: 0 }}>{origem.versao}</dd></>}
            <dt style={rotuloDaOrigem}>Motor</dt><dd style={{ margin: 0 }}>{origem.motorId ? nomes.motor.get(origem.motorId) ?? `motor ${origem.motorId}` : "sem motor"}</dd>
            <dt style={rotuloDaOrigem}>Sistema</dt><dd style={{ margin: 0 }}>{origem.sistemaId ? nomes.sistema.get(origem.sistemaId) ?? `sistema ${origem.sistemaId}` : "sem sistema"}</dd>
            <dt style={rotuloDaOrigem}>Posições</dt><dd style={{ margin: 0 }}>{posicoes.length ? posicoes.join(", ") : "nenhuma"}</dd>
          </dl>
          <p style={{ ...nota, padding: "0 0 8px" }}>
            Os modelos novos entram na mesma central e mostram as mesmas linhas. Escolha os veículos e os anos.
          </p>
        </Bloco>
      ) : (
      <>
      <Bloco titulo="A caixa">
        <Campo rotulo="Título" ajuda="O mesmo em todas as caixas do lote — ex.: fusíveis internos.">
          <input value={titulo} onChange={(e) => setTitulo(e.target.value)} style={estiloCampo} />
        </Campo>
        <Campo rotulo="Versão" ajuda="Opcional.">
          <input value={versao} onChange={(e) => setVersao(e.target.value)} style={estiloCampo} />
        </Campo>
      </Bloco>

      </>
      )}

      <Bloco titulo={`Veículos · ${veiculos.length}`}>
        <ListaDeMarcar
          mapa={nomes.veiculo}
          carregando={nomes.carregando}
          escolhidos={veiculos}
          aoMudar={setVeiculos}
        />
      </Bloco>

      {!origem && (
      <Bloco titulo="Motor e sistema">
        <Campo rotulo="Motor">
          <SeletorComBusca itens={motores} valor={motorId} aoMudar={setMotorId}
                           carregando={nomes.carregando} rotuloVazio="sem motor" rotuloAcessivel="Motor" />
        </Campo>
        <Campo rotulo="Sistema">
          <SeletorComBusca itens={sistemas} valor={sistemaId} aoMudar={setSistemaId}
                           carregando={nomes.carregando} rotuloVazio="sem sistema" rotuloAcessivel="Sistema" />
        </Campo>
      </Bloco>
      )}

      <Bloco titulo={`Anos · ${anos.length}`}>
        <Campo rotulo="Anos" ajuda="2018 2019 2020, ou a faixa 2018-2021.">
          <input value={anosTexto} onChange={(e) => setAnosTexto(e.target.value)}
                 placeholder="2018-2021" style={estiloCampo} />
        </Campo>
        {anos.length > 0 && (
          <Etiquetas>{anos.map((a) => <Etiqueta key={a}>{a}</Etiqueta>)}</Etiquetas>
        )}
      </Bloco>

      {aPartirDe && aPartirDe.fotos > 0 && (
        <label style={{ ...linhaDeMarcar, margin: "0 0 10px", padding: "7px 10px", border: "1px solid var(--line2)", borderRadius: 7, background: "var(--surface)" }}>
          <input type="checkbox" checked={copiarFotos} onChange={(e) => setCopiarFotos(e.target.checked)} />
          <span>
            Copiar as <b>{aPartirDe.fotos} foto{aPartirDe.fotos === 1 ? "" : "s"}</b> desta caixa para as novas
            <span style={{ display: "block", fontSize: 10.5, color: "var(--txt3)" }}>
              Cada caixa nova recebe uma cópia; apagar a foto de uma não tira a das outras.
            </span>
          </span>
        </label>
      )}

      {!origem && (
      <Bloco titulo={`Posições · ${posicoes.length}`}>
        <Campo rotulo="Posições (identificadores)"
               ajuda="Vão juntas em cada caixa, não multiplicam. A caixa mostra as linhas do projeto com estas posições.">
          <textarea value={identificadores} onChange={(e) => setIdentificadores(e.target.value)}
                    placeholder="F01, F02, R1" rows={2}
                    style={{ ...estiloCampo, resize: "vertical", fontFamily: "inherit" }} />
        </Campo>
        {posicoes.length > 0 && (
          <Etiquetas>{posicoes.map((p) => <Etiqueta key={p}>{p}</Etiqueta>)}</Etiquetas>
        )}
      </Bloco>
      )}

      <p style={{
        margin: "4px 0 10px", padding: "8px 10px", borderRadius: 6, fontSize: 11.5, lineHeight: 1.55,
        background: "var(--surface)", border: "1px solid var(--line2)",
      }}>
        {quantas === 0 ? (
          <span style={{ color: "var(--txt3)" }}>
            Escolha ao menos um veículo e um ano — a conta é veículos × anos.
          </span>
        ) : (
          <>
            Vai criar <b>{quantas} caixa{quantas === 1 ? "" : "s"}</b>:{" "}
            {veiculos.length} veículo{veiculos.length === 1 ? "" : "s"} × {anos.length} ano
            {anos.length === 1 ? "" : "s"}.
            {quantas > 60 && (
              <b style={{ color: "var(--erro)" }}> Criar é uma chamada; apagar são {quantas} gestos.</b>
            )}
          </>
        )}
      </p>

      <div style={{ display: "flex", gap: 6 }}>
        <Botao
          destaque
          desligado={quantas === 0 || titulo.trim() === ""}
          ocupado={criar.isPending}
          aoClicar={() => { aoAvisar(null); criar.mutate() }}
        >
          {criar.isPending ? "Criando…" : `Criar ${quantas || ""} caixa(s)`}
        </Botao>
        <Botao aoClicar={() => setAberto(false)}>Cancelar</Botao>
      </div>
    </div>
  )
}

/** Um grupo do formulário, com título pequeno — os campos ficam um embaixo do outro. */
function Bloco({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <fieldset style={{
      margin: "0 0 10px", padding: "8px 10px 2px", border: "1px solid var(--line2)",
      borderRadius: 7, background: "var(--surface)", display: "flex", flexDirection: "column",
    }}>
      <legend style={{
        padding: "0 4px", fontSize: 10, fontWeight: 600, letterSpacing: ".04em",
        textTransform: "uppercase", color: "var(--txt3)",
      }}>
        {titulo}
      </legend>
      {children}
    </fieldset>
  )
}

function Etiquetas({ children }: { children: React.ReactNode }) {
  return <div style={{ display: "flex", flexWrap: "wrap", gap: 4, margin: "-3px 0 9px" }}>{children}</div>
}

const DESENHADOS = 200

/**
 * Marcar vários num catálogo grande (1.201 veículos), à vista: filtro, lista com
 * caixas de marcar e o que já foi escolhido em etiquetas, que tiram com um ×.
 *
 * Desenha 200 por vez e diz quantos ficaram de fora; o filtro casa por palavra,
 * sem caixa e sem acento, como o resto do sistema.
 */
function ListaDeMarcar({ mapa, carregando, escolhidos, aoMudar }: {
  mapa: Map<number, string>
  carregando?: boolean
  escolhidos: number[]
  aoMudar: (ids: number[]) => void
}) {
  const [busca, setBusca] = useState("")
  const todos = useMemo(() => ordenadas(mapa), [mapa])
  const achados = useMemo(
    () => (busca.trim() ? todos.filter((o) => casaBusca(busca, o.rotulo)) : todos),
    [todos, busca])
  const marcados = new Set(escolhidos)
  const visiveis = achados.slice(0, DESENHADOS)
  const todosMarcados = visiveis.length > 0 && visiveis.every((o) => marcados.has(o.id))

  function alternar(id: number) {
    aoMudar(marcados.has(id) ? escolhidos.filter((x) => x !== id) : [...escolhidos, id])
  }

  function marcarVisiveis() {
    if (todosMarcados) {
      const sair = new Set(visiveis.map((o) => o.id))
      aoMudar(escolhidos.filter((x) => !sair.has(x)))
    } else {
      aoMudar([...escolhidos, ...visiveis.map((o) => o.id).filter((id) => !marcados.has(id))])
    }
  }

  return (
    <div style={{ marginBottom: 9 }}>
      {escolhidos.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 6 }}>
          {escolhidos.map((id) => (
            <Etiqueta key={id} aoTirar={() => alternar(id)} rotuloTirar={`Tirar ${mapa.get(id) ?? id}`}>
              {mapa.get(id) || `veículo ${id}`}
            </Etiqueta>
          ))}
          <button onClick={() => aoMudar([])} style={{
            border: 0, background: "transparent", cursor: "pointer", fontSize: 11,
            color: "var(--acao-on-soft)", fontWeight: 600, padding: "2px 4px",
          }}>
            limpar
          </button>
        </div>
      )}

      <input
        value={busca}
        onChange={(e) => setBusca(e.target.value)}
        placeholder="Filtrar veículos…"
        aria-label="Filtrar veículos"
        style={{ ...estiloCampo, marginBottom: 4 }}
      />

      <div style={{
        border: "1px solid var(--line)", borderRadius: 5, background: "var(--surface)",
        maxHeight: 190, overflowY: "auto", padding: 3,
      }}>
        {carregando ? (
          <p style={nota}>Carregando…</p>
        ) : visiveis.length === 0 ? (
          <p style={nota}>Nenhum veículo com esse texto.</p>
        ) : (
          <>
            {visiveis.length > 1 && (
              <label style={{ ...linhaDeMarcar, fontWeight: 600, color: "var(--acao-on-soft)" }}>
                <input type="checkbox" checked={todosMarcados} onChange={marcarVisiveis} />
                {todosMarcados ? "Desmarcar" : "Marcar"} {busca.trim() ? "os encontrados" : "todos"} ({visiveis.length})
              </label>
            )}
            {visiveis.map((o) => (
              <label key={o.id} style={{
                ...linhaDeMarcar,
                background: marcados.has(o.id) ? "var(--acao-soft)" : undefined,
                color: marcados.has(o.id) ? "var(--acao-on-soft)" : undefined,
              }}>
                <input type="checkbox" checked={marcados.has(o.id)} onChange={() => alternar(o.id)} />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.rotulo}</span>
              </label>
            ))}
          </>
        )}
      </div>
      <p style={{ margin: "3px 0 0", fontSize: 10, color: "var(--txt3)" }}>
        {achados.length > DESENHADOS
          ? `${achados.length} encontrados; mostrando ${DESENHADOS}. Digite para filtrar.`
          : `${achados.length} de ${todos.length}. Cada um vira uma caixa por ano.`}
      </p>
    </div>
  )
}

const linhaDeMarcar: React.CSSProperties = {
  display: "flex", alignItems: "center", gap: 7, padding: "3px 6px", borderRadius: 4,
  fontSize: 11.5, cursor: "pointer",
}

const nota: React.CSSProperties = { margin: 0, padding: "6px 8px", fontSize: 11.5, color: "var(--txt3)" }

const rotuloDaOrigem: React.CSSProperties = { color: "var(--txt3)" }

function ordenadas(mapa: Map<number, string>) {
  return [...mapa.entries()]
    .filter(([id, nome]) => id > 0 && nome.trim())
    .map(([id, rotulo]) => ({ id, rotulo }))
    .sort((a, b) => a.rotulo.localeCompare(b.rotulo, "pt-BR"))
}

/**
 * Lê as posições como a API lê: vírgula, ponto-e-vírgula ou quebra de linha. Espaço
 * não separa — há posição com espaço no meio ("SB 25"). Sem repetição, sem caixa alta.
 */
export function lerPosicoes(texto: string): string[] {
  const vistas = new Set<string>()
  const lidas: string[] = []
  for (const pedaco of texto.split(/[,;\n\r]+/)) {
    const p = pedaco.trim()
    if (!p || vistas.has(p.toLowerCase())) continue
    vistas.add(p.toLowerCase())
    lidas.push(p)
  }
  return lidas
}

/**
 * Lê os anos do jeito que as pessoas escrevem: separados por espaço, vírgula ou
 * ponto-e-vírgula, e faixas com hífen. Devolve ordenado e sem repetição.
 */
export function lerAnos(texto: string): number[] {
  const achados = new Set<number>()

  for (const pedaco of texto.split(/[,;\s]+/)) {
    const t = pedaco.trim()
    if (!t) continue

    const faixa = /^(\d{4})\s*[-–]\s*(\d{4})$/.exec(t)
    if (faixa) {
      const de = Number(faixa[1])
      const ate = Number(faixa[2])
      // Faixa invertida e erro de digitacao, nao pedido: 2021-2018 vale o mesmo.
      for (let a = Math.min(de, ate); a <= Math.max(de, ate); a++) achados.add(a)
      continue
    }

    if (/^\d{4}$/.test(t)) achados.add(Number(t))
  }

  return [...achados].sort((a, b) => a - b)
}
